import * as moo from 'moo';
import { Week, DayDetails, Units } from 'types/app';

export function kmToMiles(value: number): number {
  return value * 0.62137;
}

export function miToKm(value: number): number {
  return value / 0.62137;
}

export function mToKm(value: number): number {
  return value / 1000;
}

export function kmToM(value: number): number {
  return value * 1000;
}

export function mToMiles(value: number): number {
  return (value / 1000) * 0.62137;
}

export function milesToM(value: number): number {
  return (value / 0.62137) * 1000;
}

export function getWeekDistance(
  week: Week<DayDetails>,
  units: Units,
): number[] {
  let min = 0;
  let max = 0;
  let hasRange = false;

  for (const day of week.days) {
    const e = day.event;
    if (!e || !e.dist) continue;

    if (typeof e.dist === 'number') {
      // Convert from source units to display units
      const originalDist = e.dist as number;
      let convertedDist = originalDist;
      if (e.sourceUnits === 'mi' && units === 'km') {
        convertedDist = miToKm(originalDist);
      } else if (e.sourceUnits === 'km' && units === 'mi') {
        convertedDist = kmToMiles(originalDist);
      } else if (e.sourceUnits === 'm' && units === 'km') {
        convertedDist = mToKm(originalDist);
      } else if (e.sourceUnits === 'm' && units === 'mi') {
        convertedDist = mToMiles(originalDist);
      } else if (e.sourceUnits === 'km' && units === 'm') {
        convertedDist = kmToM(originalDist);
      } else if (e.sourceUnits === 'mi' && units === 'm') {
        convertedDist = milesToM(originalDist);
      }
      min += convertedDist;
      max += convertedDist;
    } else if (Array.isArray(e.dist) && e.dist.length === 2) {
      // Convert from source units to display units
      const [originalLo, originalHi] = e.dist;
      let convertedLo = originalLo;
      let convertedHi = originalHi;
      if (e.sourceUnits === 'mi' && units === 'km') {
        convertedLo = miToKm(originalLo);
        convertedHi = miToKm(originalHi);
      } else if (e.sourceUnits === 'km' && units === 'mi') {
        convertedLo = kmToMiles(originalLo);
        convertedHi = kmToMiles(originalHi);
      } else if (e.sourceUnits === 'm' && units === 'km') {
        convertedLo = mToKm(originalLo);
        convertedHi = mToKm(originalHi);
      } else if (e.sourceUnits === 'm' && units === 'mi') {
        convertedLo = mToMiles(originalLo);
        convertedHi = mToMiles(originalHi);
      } else if (e.sourceUnits === 'km' && units === 'm') {
        convertedLo = kmToM(originalLo);
        convertedHi = kmToM(originalHi);
      } else if (e.sourceUnits === 'mi' && units === 'm') {
        convertedLo = milesToM(originalLo);
        convertedHi = milesToM(originalHi);
      }
      min += convertedLo;
      max += convertedHi;
      hasRange = true;
    }
  }
  return hasRange ? [min, max] : [max];
}

export function renderDist(value: number[], from: Units, to: Units): string {
  function convert(val: number): number {
    if (from === to) return val;

    // Convert from meters
    if (from === 'm' && to === 'km') return val / 1000;
    if (from === 'm' && to === 'mi') return (val / 1000) * 0.62137;

    // Convert to meters
    if (from === 'km' && to === 'm') return val * 1000;
    if (from === 'mi' && to === 'm') return (val / 0.62137) * 1000;

    // Convert between km and mi
    if (from === 'mi' && to === 'km') return val / 0.62137;
    if (from === 'km' && to === 'mi') return val * 0.62137;

    return val;
  }

  function format(val: number): string {
    // Keep small values readable (e.g., swimming sessions in km).
    // - < 10: show 1 decimal if needed
    // - >= 10: round to whole
    if (Math.abs(val) < 10) {
      const rounded = Math.round(val * 10) / 10;
      return Number.isInteger(rounded)
        ? rounded.toFixed(0)
        : rounded.toFixed(1);
    }
    return Math.round(val).toFixed(0);
  }

  if (Array.isArray(value)) {
    if (value.length === 1) {
      const v = convert(value[0]);
      return format(v) + ' ' + to;
    }
    if (value.length === 2) {
      const [v1, v2] = value.map(convert);
      return format(v1) + '-' + format(v2) + ' ' + to;
    }
  }
  return '';
}

let dlexer = moo.compile({
  with_range: [
    {
      match: /{\d+-\d+:\d+-\d+}/,
      value: (x) => x.slice(1, -1),
    },
  ],
  with_conversion: [
    {
      match: /{\d+(?:\.\d+)?:\d+(?:\.\d+)?}/,
      value: (x) => x.slice(1, -1),
    },
  ],
  single: [
    {
      match: /{\d+(?:\.\d+)?}/,
      value: (x) => x.slice(1, -1),
    },
  ],
  text: /[^{\n}]+/,
  NL: { match: /\n/, lineBreaks: true },
});

// function getUnitLabel(unit: Units): string {
//   return unit === "km" ? "km" : "mi";
// }

function handle_conversions(input: string, from: Units, to: Units): string {
  let result = '';
  dlexer.reset(input);
  let t = dlexer.next();

  while (t) {
    if (t.type === 'single') {
      // e.g. {9}
      const out = renderDist([Number(t.value)], from, to);
      result += out;
    } else if (t.type === 'with_conversion') {
      // e.g. {9:14}
      const [fromVal, toVal] = t.value.split(':').map(Number);
      const out =
        from === to
          ? renderDist([fromVal], from, from)
          : renderDist([toVal], to, to);
      result += out;
    } else if (t.type === 'with_range') {
      // e.g. {8-9:13-14}
      const [fromRange, toRange] = t.value.split(':');
      const [fromStart, fromEnd] = fromRange.split('-').map(Number);
      const [toStart, toEnd] = toRange.split('-').map(Number);
      const out =
        from === to
          ? renderDist([fromStart, fromEnd], from, from)
          : renderDist([toStart, toEnd], to, to);
      result += out;
    } else {
      result += t.value;
    }
    t = dlexer.next();
  }
  return result;
}

export function renderStr(input: string, from: Units, to: Units): string {
  return handle_conversions(input, from, to);
}

export function render(
  input: DayDetails,
  from: Units,
  to: Units,
): [string, string] {
  // [title, desc]
  let title = handle_conversions(input.title, from, to);
  let desc = handle_conversions(input.desc, from, to);
  return [title, desc];
}
